import * as React from 'react'
import { type Block } from '@basehub/data/replicache/index'
import type { BlocksProps } from '.'
import { Input as TextBlock } from './text'
import { Input as NumberBlock } from './number'
import { Input as SelectBlock } from './select'
import { Input as DateBlock } from './date'
import { Input as MediaBlock } from './media'
import { Input as BooleanBlock } from './boolean'
import { Input as CodeSnippetBlock } from './code-snippet'
import { ListBlockLight } from './list'
import { Link as NestedDocumentBlock } from './document/nested'
import { Input as ReferenceBlock } from './reference'
import { Input as ColorBlock } from './color'
import { Input as OgImageBlock } from './og-image'
import { Input as VariantBlock } from './variant-set'
import { Input as EventBlock } from './event'
import { Input as WorkflowBlock } from './workflow'
import {
  commentExtensionsForTransformations,
  jsonToPlainText,
  stateToJson,
} from '@basehub/tiptap-api-utils'
import { RichTextPreviewBlock } from './rich-text/preview'

/* -------------------------------------------------------------------------------------------------
 * Preview Block Switch
 * -----------------------------------------------------------------------------------------------*/

export const isValidLayoutBlock = (
  parent: Block
): parent is BlocksProps['block'] =>
  parent.type === 'instance' ||
  parent.type === 'component' ||
  parent.type === 'document' ||
  parent.type === 'reference'

export const PreviewBlockSwitch = React.memo(
  ({
    block,
    path,
    parent,
    ...props
  }: {
    block: Block
    path: string
    parent: BlocksProps['block']
  } & {
    className?: string
  }) => {
    const key = `${block.id}-${block.type}`
    switch (block.type) {
      case 'text': {
        return (
          <TextBlock
            key={key}
            block={block}
            path={path}
            parent={parent}
            readOnly={true}
            className="whitespace-nowrap overflow-hidden overflow-ellipsis"
            maxRows={1}
            {...props}
          />
        )
      }
      case 'number':
        return (
          <NumberBlock
            key={key}
            block={block}
            parent={parent}
            path={path}
            readOnly={true}
            className="whitespace-nowrap overflow-hidden overflow-ellipsis"
            {...props}
          />
        )
      case 'select':
        return (
          <SelectBlock
            key={key}
            block={block}
            path={path}
            parent={parent}
            readOnly={true}
            {...props}
          />
        )
      case 'comment':
        return (
          <span
            className="cursor-text text-dark-faint text-sm"
            dangerouslySetInnerHTML={{
              __html: jsonToPlainText(
                stateToJson(block.value.state, {
                  cacheKey: JSON.stringify(block.value.state),
                }) as any,
                commentExtensionsForTransformations
              ).replace(/\n/g, '<br/>'),
            }}
          ></span>
        )
      case 'rich-text': {
        return (
          <RichTextPreviewBlock
            path={path}
            parent={parent}
            block={block}
            _key={key}
            {...props}
          />
        )
      }
      case 'date': {
        return (
          <DateBlock
            key={key}
            block={block}
            parent={parent}
            path={path}
            readOnly={true}
            {...props}
          />
        )
      }
      case 'media': {
        return (
          <MediaBlock
            key={key}
            block={block}
            path={path}
            parent={parent}
            readOnly={true}
            {...props}
          />
        )
      }
      case 'boolean': {
        return (
          <BooleanBlock
            key={key}
            block={block}
            path={path}
            parent={parent}
            disabled={true}
            {...props}
          />
        )
      }
      case 'code-snippet': {
        return (
          <CodeSnippetBlock
            key={key}
            parent={parent}
            block={block}
            path={path}
            readOnly={true}
            showControls={false}
            {...props}
          />
        )
      }
      case 'og-image': {
        return (
          <OgImageBlock
            key={key}
            block={block}
            path={path}
            parent={parent}
            readOnly={true}
            {...props}
          />
        )
      }
      case 'list': {
        return <ListBlockLight key={key} block={block} {...props} />
      }
      case 'instance': {
        return (
          <NestedDocumentBlock
            mode="withActions"
            wrapperClassName="!mx-0"
            key={key}
            block={block}
            parentBlock={parent}
            path={path}
            {...props}
          />
        )
      }
      case 'component': {
        return (
          <NestedDocumentBlock
            mode="withActions"
            wrapperClassName="!mx-0"
            key={key}
            block={block}
            path={path}
            parentBlock={parent}
            {...props}
          />
        )
      }
      case 'document': {
        return (
          <NestedDocumentBlock
            mode="withActions"
            wrapperClassName="!mx-0"
            key={key}
            parentBlock={parent}
            block={block}
            path={path}
            {...props}
          />
        )
      }
      case 'reference': {
        return (
          <ReferenceBlock
            key={key}
            path={path}
            block={block}
            readOnly={true}
            parent={parent}
            {...props}
          />
        )
      }
      case 'color': {
        return (
          <ColorBlock
            key={key}
            parent={parent}
            block={block}
            path={path}
            readOnly={true}
            {...props}
          />
        )
      }
      case 'variant-set': {
        return (
          <VariantBlock
            key={key}
            block={block}
            path={path}
            parent={parent}
            // readOnly={true}
            {...props}
          />
        )
      }
      case 'event': {
        return (
          <EventBlock
            key={key}
            block={block}
            path={path}
            parentBlock={parent}
            {...props}
          />
        )
      }
      case 'workflow': {
        return <WorkflowBlock block={block} path={path} {...props} />
      }
      default:
        return null
    }
  }
)
